import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { formatPublishedAt } from '../../lib/magazineApi';

/*
  ArticleCard — single canonical card used everywhere in the Magazine.
  Variants:
    • default  → standard editorial card with cover, eyebrow, title, excerpt
    • feature  → larger hero-style card (used on the magazine home)
    • compact  → no cover, used in the article-page related rail
*/

function CategoryEyebrow({ category }) {
  if (!category) return null;
  return (
    <span
      className="inline-block text-[10.5px] tracking-[0.18em] font-bold uppercase"
      style={{ color: category.accent_color || '#F77F11' }}
    >
      {category.name}
    </span>
  );
}

function MetaLine({ article }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-neutral-500 mt-3">
      {article.author?.avatar_url && (
        <img
          src={article.author.avatar_url}
          alt={article.author.name}
          loading="lazy"
          className="w-5 h-5 rounded-full object-cover ring-1 ring-neutral-200"
        />
      )}
      {article.author?.name && <span className="font-medium text-neutral-700">{article.author.name}</span>}
      {article.author?.name && <span className="w-1 h-1 rounded-full bg-neutral-300" />}
      {article.published_at && <span>{formatPublishedAt(article.published_at)}</span>}
      <span className="w-1 h-1 rounded-full bg-neutral-300" />
      <span className="inline-flex items-center gap-1">
        <Clock className="w-3 h-3" />
        {article.reading_time} min
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = 'default' }) {
  if (!article) return null;
  const href = `/magazine/${article.slug}`;

  if (variant === 'feature') {
    return (
      <Link
        to={href}
        className="group block rounded-3xl overflow-hidden border border-neutral-200 bg-white hover:border-orange-200 hover:shadow-[0_28px_70px_-30px_rgba(247,127,17,0.35)] hover:-translate-y-0.5 transition-all"
        data-testid={`magazine-card-${article.slug}`}
      >
        <div className="grid md:grid-cols-2">
          <div className="aspect-[16/10] md:aspect-auto overflow-hidden bg-neutral-100">
            {article.cover_image && (
              <img
                src={article.cover_image}
                alt={article.cover_alt || article.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              />
            )}
          </div>
          <div className="p-7 lg:p-10 flex flex-col justify-center">
            <CategoryEyebrow category={article.category} />
            <h2 className="mt-3 text-[26px] lg:text-[30px] font-bold text-neutral-900 group-hover:text-[#F77F11] transition-colors leading-[1.15] tracking-[-0.018em]">
              {article.title}
            </h2>
            <p className="mt-3 text-[15px] text-neutral-600 leading-relaxed line-clamp-3">
              {article.excerpt}
            </p>
            <MetaLine article={article} />
            <span className="inline-flex items-center gap-1.5 mt-5 text-[13.5px] font-semibold text-[#F77F11]">
              Read article <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={href}
        className="group block py-4 border-b border-neutral-200 last:border-b-0"
        data-testid={`magazine-card-${article.slug}`}
      >
        <CategoryEyebrow category={article.category} />
        <h3 className="mt-1.5 text-[15px] font-semibold text-neutral-900 group-hover:text-[#F77F11] transition-colors leading-snug tracking-[-0.005em]">
          {article.title}
        </h3>
        <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-neutral-500">
          <Clock className="w-3 h-3" />
          {article.reading_time} min read
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className="group block rounded-2xl overflow-hidden border border-neutral-200 bg-white hover:border-orange-200 hover:shadow-[0_22px_55px_-25px_rgba(247,127,17,0.35)] hover:-translate-y-0.5 transition-all"
      data-testid={`magazine-card-${article.slug}`}
    >
      <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
        {article.cover_image && (
          <img
            src={article.cover_image}
            alt={article.cover_alt || article.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
          />
        )}
      </div>
      <div className="p-5">
        <CategoryEyebrow category={article.category} />
        <h3 className="mt-2 font-semibold text-[17px] text-neutral-900 group-hover:text-[#F77F11] transition-colors leading-snug tracking-[-0.01em]">
          {article.title}
        </h3>
        <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>
        <MetaLine article={article} />
        <span className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-semibold text-[#F77F11]">
          Read article <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}
